import {Button, Dropdown, Input, ToastType} from '@sportcast/ui-react';
import i18n from 'i18next';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Navigate} from 'react-router-dom';
import {v4} from 'uuid';
import {updateAccount} from '../../fetch/accountApi';
import {hideLoader} from '../../redux/action/loaderAction';
import {loginAction} from '../../redux/action/loginAction';
import {setMessage} from '../../redux/action/messageAction';
import {useAppDispatch} from '../../redux/hooks';
import {RootState} from '../../redux/rootReducer';
import {AccountType} from '../../types/AccountType';
import {Salutation} from '../../types/enums/Salutation';
import ChangePasswordDialog from './ChangePasswordDialog';
import './EditProfile.scss';

type AccountValidation = {
    firstName: boolean,
    lastName: boolean,
    password: boolean
}

const EditProfile = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const userData = useSelector((state: RootState) => state.login.userData);
    const [account, setAccount] = useState<AccountType>(userData || {} as AccountType);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [validation, setValidation] = useState<AccountValidation>({} as AccountValidation);
    const [showValidation, setShowValidation] = useState<boolean>(false);

    useEffect(() => {
        if (userData) {
            setAccount(userData);
        }
    }, [userData]);

    useEffect(() => {
        setValidation({
            firstName: !!account.firstName,
            lastName: !!account.lastName,
            password: !!account.password
        });
    }, [account]);

    const onChange = (key: string, value: string | number) => {
        setAccount({
            ...account,
            [key]: String(value)
        });
    };

    const salutationOptions = Object.keys(Salutation).map(salutation => ({
        value: salutation,
        label: t(`Salutation.${salutation}`)
    }));

    const save = () => {
        setShowValidation(true);

        if (Object.values(validation).every(fieldValidation => fieldValidation)) {
            updateAccount(account, dispatch).then(response => {
                if (response.success) {
                    const {password: _password, ...updatedUserData} = account;
                    dispatch(loginAction(updatedUserData as AccountType));
                    dispatch(setMessage({
                        i18nMessage: 'message.SAVE_SUCCESS',
                        type: ToastType.SUCCESS,
                        messageId: v4()
                    }));
                    onChange('password', '');
                    setShowValidation(false);
                }
            }).catch(error => {
                if (error.status === 417) {
                    dispatch(setMessage({
                        message: i18n.t('error.WRONG_PASSWORD'),
                        type: ToastType.ERROR,
                        messageId: v4(),
                        autoClose: true
                    }));
                    dispatch(hideLoader());
                    setValidation(oldValidation => ({
                        ...oldValidation,
                        password: false
                    }));
                }
            });
        }
    };

    if (!userData) {
        return <Navigate to="/login" replace/>;
    }

    const isInternalUser = !(userData as AccountType).isExternal;

    return (
        <div className="edit-profile">
            <h5 className="mb-2">{t('profile.TITLE')}</h5>
            <Dropdown
                id="salutation"
                onChange={value => onChange('salutation', value.value)}
                label={t('profile.SALUTATION')}
                options={salutationOptions}
                value={account.salutation}
                disabled={isInternalUser}
            />
            <Input
                id="firstName"
                onChange={onChange}
                value={account.firstName}
                label={t('profile.FIRST_NAME')}
                required={true}
                errorMessage={showValidation && !validation.firstName ? t('error.REQUIRED') : undefined}
                disabled={isInternalUser}
            />

            <Input
                id="lastName"
                onChange={onChange}
                value={account.lastName}
                label={t('profile.LAST_NAME')}
                required={true}
                errorMessage={showValidation && !validation.lastName ? t('error.REQUIRED') : undefined}
                disabled={isInternalUser}
            />

            <Input
                id="email"
                type="email"
                onChange={() => {
                }}
                value={account.email}
                label={t('profile.EMAIL')}
                disabled={true}
            />

            <Input
                id="phone"
                onChange={onChange}
                value={account.phone}
                label={t('profile.PHONE')}
                disabled={isInternalUser}
            />
            <Input
                id="mobile"
                onChange={onChange}
                value={account.mobile}
                label={t('profile.MOBILE')}
                disabled={isInternalUser}
            />
            {
                !isInternalUser ?
                    <>
                        <Input
                            id="password"
                            type="password"
                            label={t('forgotPassword.PASSWORD')}
                            required={true}
                            onChange={onChange}
                            value={account.password}
                            errorMessage={showValidation && !validation.password ? t('error.REQUIRED') : undefined}
                        />
                        <div className="button-row">
                            <Button
                                variant="outline"
                                onClick={() => setShowPasswordDialog(true)}
                                label={t('profile.CHANGE_PASSWORD')}
                            />
                            <Button
                                variant="primary"
                                label={t('button.SAVE')}
                                onClick={save}
                            />
                        </div>

                        <ChangePasswordDialog
                            show={showPasswordDialog}
                            handleClose={() => setShowPasswordDialog(false)}
                        />
                    </>
                    :
                    <div className="text-muted mt-4 small">{t('profile.INTERNAL_USER_INFO')}</div>
            }
        </div>
    );
};

export default EditProfile;
